import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ResourceProvider } from "@/Common/Resources/StaticResources";

const splitStyleRegex = /(<[biu]>{{.*?}}<\/[biu]>)/g;
const styleRegexMap = {
    italic: { style: { fontStyle: "italic" }, regex: /<i>{{(?<key>.*?)}}<\/i>/g },
    bold: { style: { fontWeight: "bolder" }, regex: /<b>{{(?<key>.*?)}}<\/b>/g },
    underline: { style: { textDecoration: "underline" }, regex: /<u>{{(?<key>.*?)}}<\/u>/g }
};

/**
 * A component used to apply different styles to vocab without splitting them up.
 * To transform part of a vocab, add the key of the vocab to apply the transform and wrap it in the corresponding tags. This is done in the vocab.resx file for each locale.
 * i.e. MessageLabel: "Hello world <b>{{boldMessage}}</b>", BoldMessage: "I am bold" will be rendered as Hello world <span style="font-weight: bolder">I am bold</span>
 *
 * @param pr
 * @returns The customized component
 */
export function CustomizedVocab({ labelKey, tokens, locale, resourceProvider = null }: { labelKey: string; tokens?: object, locale?: Locale, resourceProvider?: ResourceProvider | null }): JSX.Element {
    const { getText } = useTranslation(resourceProvider);

    const components = useMemo(() => {
        const id = GuidHelper.NewGuid();
        const vocab = getText(labelKey, tokens, locale);
        const splits = vocab.split(splitStyleRegex);

        return (
            <>
                {splits.map((value, index) => {
                    for (const [, { style, regex }] of Object.entries(styleRegexMap)) {
                        const matches = regex.exec(value);
                        if (matches) {
                            const labelKey = matches.groups?.key;
                            return (
                                <span key={`${id}-${index}`} style={style}>
                                    {labelKey ? getText(labelKey) : value}
                                </span>
                            );
                        }
                    }
                    return value; // Return the original value if no match
                })}
            </>
        );
    }, [getText, labelKey]);

    return components;
}
